'use client';

import React, { useState } from 'react';
import { Users, CheckSquare, TrendingUp, ChevronRight, Calendar, X } from 'lucide-react';
import { TEAM_MEMBERS, PROJECTS, type TeamMember } from '@/lib/mockData';
import PriorityBadge from '@/components/ui/PriorityBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import Link from 'next/link';

export default function TeamContent() {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  const getMemberTasks = (memberId: string) =>
    PROJECTS.flatMap((p) =>
      p.tasks
        .filter((t) => t.assigneeId === memberId)
        .map((t) => ({ ...t, projectName: p.name }))
    );

  const getWorkloadColor = (activeTasks: number) => {
    if (activeTasks >= 7) return 'text-red-500';
    if (activeTasks >= 5) return 'text-amber-600';
    return 'text-green-600';
  };

  const getWorkloadLabel = (activeTasks: number) => {
    if (activeTasks >= 7) return 'Overloaded';
    if (activeTasks >= 5) return 'Busy';
    return 'Available';
  };

  const getWorkloadBg = (activeTasks: number) => {
    if (activeTasks >= 7) return 'bg-red-50 text-red-600';
    if (activeTasks >= 5) return 'bg-amber-50 text-amber-700';
    return 'bg-green-50 text-green-700';
  };

  const memberTasks = selectedMember ? getMemberTasks(selectedMember.id) : [];

  return (
    <div className="p-6 lg:p-8 max-w-screen-2xl mx-auto fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Team</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {TEAM_MEMBERS.length} members · {PROJECTS.length} active projects
          </p>
        </div>
        <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2">
          <Users size={15} className="text-primary" />
          <span className="text-sm font-medium text-foreground">{TEAM_MEMBERS.length} members</span>
        </div>
      </div>

      {/* Team summary row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          {
            id: 'ts-total',
            label: 'Total Members',
            value: TEAM_MEMBERS.length,
            icon: <Users size={18} />,
            color: 'bg-primary/10 text-primary',
          },
          {
            id: 'ts-tasks',
            label: 'Total Active Tasks',
            value: TEAM_MEMBERS.reduce((s, m) => s + m.activeTasks, 0),
            icon: <CheckSquare size={18} />,
            color: 'bg-blue-50 text-blue-600',
          },
          {
            id: 'ts-completed',
            label: 'Completed This Week',
            value: TEAM_MEMBERS.reduce((s, m) => s + m.completedThisWeek, 0),
            icon: <TrendingUp size={18} />,
            color: 'bg-green-50 text-green-600',
          },
          {
            id: 'ts-velocity',
            label: 'Avg. Tasks / Member',
            value: (TEAM_MEMBERS.reduce((s, m) => s + m.activeTasks, 0) / TEAM_MEMBERS.length).toFixed(1),
            icon: <ChevronRight size={18} />,
            color: 'bg-amber-50 text-amber-600',
          },
        ].map((stat) => (
          <div key={stat.id} className="bg-card rounded-xl border border-border p-4">
            <div className={`w-9 h-9 rounded-lg ${stat.color} flex items-center justify-center mb-3`}>
              {stat.icon}
            </div>
            <p className="text-2xl font-bold font-tabular text-foreground">{stat.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Member cards */}
        <div className="xl:col-span-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {TEAM_MEMBERS.map((member) => {
              const tasks = getMemberTasks(member.id);
              const doneTasks = tasks.filter((t) => t.status === 'Done').length;
              const isSelected = selectedMember?.id === member.id;

              return (
                <button
                  key={`member-card-${member.id}`}
                  onClick={() => setSelectedMember(isSelected ? null : member)}
                  className={`bg-card rounded-xl border p-5 text-left hover:shadow-card-hover transition-all duration-200 group ${
                    isSelected
                      ? 'border-primary/40 ring-2 ring-primary/20' :'border-border hover:border-primary/20'
                  }`}
                >
                  {/* Avatar + name */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-base font-bold flex-shrink-0"
                        style={{ backgroundColor: member.avatarColor }}
                      >
                        {member.avatarInitials}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                          {member.name}
                        </p>
                        <p className="text-xs text-muted-foreground">{member.role}</p>
                      </div>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getWorkloadBg(member.activeTasks)}`}>
                      {getWorkloadLabel(member.activeTasks)}
                    </span>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="text-center">
                      <p className={`text-xl font-bold font-tabular ${getWorkloadColor(member.activeTasks)}`}>
                        {member.activeTasks}
                      </p>
                      <p className="text-xs text-muted-foreground">Active</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold font-tabular text-green-600">
                        {member.completedThisWeek}
                      </p>
                      <p className="text-xs text-muted-foreground">Done/wk</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold font-tabular text-foreground">
                        {tasks.length}
                      </p>
                      <p className="text-xs text-muted-foreground">Total</p>
                    </div>
                  </div>

                  {/* Workload bar */}
                  <div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span>Workload</span>
                      <span className="font-medium">{doneTasks}/{tasks.length} done</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full progress-bar-fill"
                        style={{
                          width: tasks.length > 0 ? `${Math.round((doneTasks / tasks.length) * 100)}%` : '0%',
                          backgroundColor: member.avatarColor,
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs text-muted-foreground">{member.email}</span>
                    <ChevronRight
                      size={14}
                      className={`text-muted-foreground transition-transform ${isSelected ? 'rotate-90 text-primary' : ''}`}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Member task detail panel */}
        <div className="xl:col-span-1">
          {selectedMember ? (
            <div className="bg-card rounded-xl border border-border overflow-hidden sticky top-6">
              {/* Panel header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                    style={{ backgroundColor: selectedMember.avatarColor }}
                  >
                    {selectedMember.avatarInitials}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{selectedMember.name}</p>
                    <p className="text-xs text-muted-foreground">{memberTasks.length} tasks</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedMember(null)}
                  className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                >
                  <X size={15} className="text-muted-foreground" />
                </button>
              </div>

              {/* Task list */}
              <div className="divide-y divide-border max-h-[520px] overflow-y-auto scrollbar-thin">
                {memberTasks.length === 0 ? (
                  <div className="py-10 text-center">
                    <p className="text-sm text-muted-foreground">No tasks assigned.</p>
                  </div>
                ) : (
                  memberTasks.map((task) => (
                    <Link
                      key={`member-task-${task.id}`}
                      href="/task-detail"
                      className="flex items-start gap-3 px-5 py-3.5 hover:bg-muted/40 transition-colors group"
                    >
                      <div
                        className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${
                          task.status === 'Done' ?'bg-green-500'
                            : task.status === 'In Progress' ?'bg-blue-500'
                            : task.status === 'In Review' ?'bg-amber-500' :'bg-slate-300'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
                          {task.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs text-muted-foreground">{task.projectName}</span>
                          <PriorityBadge priority={task.priority} size="sm" />
                        </div>
                        <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                          <Calendar size={11} />
                          <span>{task.dueDate}</span>
                        </div>
                      </div>
                      <StatusBadge status={task.status} />
                    </Link>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="bg-card rounded-xl border border-border p-8 text-center">
              <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-3">
                <Users size={24} className="text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">Select a team member</p>
              <p className="text-xs text-muted-foreground">
                Click on a member card to see their assigned tasks and workload details.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}